import { Controller } from "stimulus"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "@geoman-io/leaflet-geoman-free"
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css"

// Dynamic map renderer + editor + fog of war.
// - All viewers: pan/zoom, see features, click a feature to read its title/notes.
// - Cartographers: a labeled toolbar drives Geoman (pin/area/line/box, reshape/move/
//   delete) with undo/redo; click a feature to edit label/notes/color/icon/secret.
// - GMs: fog toggle + "reveal" regions that clear it + a player-view preview.
//   Under-fog content is withheld server-side (see MapFogVisibility).
const DEFAULT_COLOR = "#65b4c6" // Chronica "blue"
const REVEAL_COLOR = "#76c29d"  // Chronica "green"
const SECRET_ICON = "fad fa-eye-evil" // app's standard "secret" icon

export default class extends Controller {
  static targets = ["canvas", "editModal"]

  connect() {
    this.featuresUrl = this.data.get("featuresUrl")
    this.updateUrl = this.data.get("updateUrl")
    this.linkablesUrl = this.data.get("linkablesUrl")
    this.editable = this.data.get("editable") === "true"
    this.isGm = this.data.get("gm") === "true"
    this.fogEnabled = this.data.get("fog") === "true"
    this.fogColor = this.data.get("fogColor") || "#1e293b"
    this.fogOpacity = parseFloat(this.data.get("fogOpacity"))
    if (isNaN(this.fogOpacity)) this.fogOpacity = 1.0
    this.selectedColor = DEFAULT_COLOR
    this.selectedIconId = null
    this.fogEditMode = false // GM: dedicated mode where ONLY reveals are editable
    this.textMode = false    // next-drawn marker becomes a text label, not a pin
    this.fogShow = true   // GM: show a transparent fog overlay while editing
    this.preview = false  // GM: preview the exact (opaque) player view
    this.revealLayers = new Set()
    this.featureLayers = new Set()
    this.categories = { pin: true, shape: true, text: true } // view toggles (all viewers)
    this.armedLinkable = null // drag-to-link drawer: tapped item awaiting a map tap (touch)
    this.undoStack = []
    this.redoStack = []
    this.ensureStyles()
    // Clean up after the editor modal closes by ANY means (Save, Cancel, X, backdrop,
    // Esc) so the next open starts fresh.
    if (this.hasEditModalTarget) {
      this.editModalTarget.addEventListener("hidden.bs.modal", () => this.afterModalHidden())
    }
    this.initMap()
    this.setupAutoHeight()
  }

  afterModalHidden() {
    document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove())
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("padding-right")
    this.editingLayer = null
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.onWindowResize) {
      window.removeEventListener("resize", this.onWindowResize)
      this.onWindowResize = null
    }
    if (this.onWindowLoad) {
      window.removeEventListener("load", this.onWindowLoad)
      this.onWindowLoad = null
    }
    if (this.sizeTimers) {
      this.sizeTimers.forEach(clearTimeout)
      this.sizeTimers = null
    }
    if (this.toolsObserver) {
      this.toolsObserver.disconnect()
      this.toolsObserver = null
    }
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  // Grow the map canvas to fill the space below it down to the viewport bottom,
  // and re-fit Leaflet whenever that space changes — collapsing/expanding the Map
  // Tools card (watched via ResizeObserver) or resizing the window.
  setupAutoHeight() {
    this.sizeCanvas()
    this.onWindowResize = () => this.sizeCanvas()
    window.addEventListener("resize", this.onWindowResize)

    // Re-sync after the page fully settles. Late-loading content above the map
    // (ads, web fonts, hero images) can push the map DOWN after Leaflet cached its
    // position — which makes clicks/draws land offset (typically southward) until
    // invalidateSize runs again. This is the usual dev-vs-prod difference.
    this.onWindowLoad = () => this.sizeCanvas()
    window.addEventListener("load", this.onWindowLoad)
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.sizeCanvas())
    this.sizeTimers = [setTimeout(() => this.sizeCanvas(), 400), setTimeout(() => this.sizeCanvas(), 1200)]

    if (window.ResizeObserver) {
      // Watch the toolbar card (collapse/expand) AND the page body, so any layout
      // shift above the map re-runs sizeCanvas() and re-syncs Leaflet's origin.
      this.toolsObserver = new ResizeObserver(() => this.sizeCanvas())
      const card = this.element.querySelector(".dm-tools-card")
      if (card) this.toolsObserver.observe(card)
      this.toolsObserver.observe(document.body)
    }
  }

  sizeCanvas() {
    if (!this.map || !this.hasCanvasTarget) return
    const gap = 16 // breathing room below the map
    const top = this.canvasTarget.getBoundingClientRect().top
    const h = Math.max(360, Math.round(window.innerHeight - top - gap))
    // Only write when it actually changes (avoids a body-observer feedback loop);
    // always invalidateSize so Leaflet re-reads the container's current position.
    if (this.canvasTarget.style.height !== `${h}px`) this.canvasTarget.style.height = `${h}px`
    this.map.invalidateSize(false)
  }

  ensureStyles() {
    if (document.getElementById("dynamic-map-styles")) return
    const css =
      ".dynamic-map-label.leaflet-tooltip{background:#fff;color:#3a2c18;border:0;border-radius:9px;" +
      "box-shadow:0 1px 3px rgba(0,0,0,0.4);font-size:11px;padding:2px 7px;font-weight:500;white-space:nowrap;}" +
      ".dynamic-map-label.leaflet-tooltip:before{display:none;border:0;}" +
      ".dynamic-map-pin{background:transparent;border:0;}" +
      ".dynamic-map-text{background:transparent;border:0;width:auto!important;height:auto!important;}" +
      ".dynamic-map-fog-label{color:rgba(255,255,255,0.82);font-size:22px;font-weight:600;letter-spacing:2px;" +
      "text-shadow:0 1px 4px rgba(0,0,0,0.7);white-space:nowrap;text-align:center;pointer-events:none;}" +
      // combo feature popup: flush wrapper, colored accent edge, header/footer dividers, scroll gutter
      ".dynamic-map-popup .leaflet-popup-content-wrapper{padding:0;border-radius:10px;overflow:hidden;box-shadow:0 8px 26px rgba(0,0,0,0.34);}" +
      ".dynamic-map-popup .leaflet-popup-content{margin:0;width:auto!important;}" +
      ".dm-pop{display:flex;color:#252422;min-width:248px;}" +
      ".dm-pop-accent{width:6px;flex:none;align-self:stretch;}" +
      ".dm-pop-inner{flex:1;min-width:0;}" +
      ".dm-pop-hd{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;padding:12px 14px 10px;border-bottom:1px solid #e3e1da;}" +
      ".dm-pop-heading{min-width:0;}" +
      ".dm-pop-kind{font-size:10px;font-weight:700;letter-spacing:0.11em;text-transform:uppercase;color:#9a9a9a;margin-bottom:1px;}" +
      ".dm-pop-title{font-size:16px;font-weight:600;line-height:1.2;margin:0;}" +
      ".dm-pop-x{flex:none;width:24px;height:24px;border-radius:50%;border:1px solid #dee2e6;background:#fff;color:#9a9a96;font-size:14px;line-height:1;padding:0;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all 0.12s;}" +
      ".dm-pop-x:hover{background:#ed4033;border-color:#ed4033;color:#fff;}" +
      ".dm-pop-body{padding:12px 14px 4px;}" +
      ".dm-pop-notes{font-size:13px;line-height:1.6;color:#4a4a46;max-height:160px;overflow-y:auto;padding-right:12px;}" +
      ".dm-pop-linklabel{font-size:10px;font-weight:700;letter-spacing:0.11em;text-transform:uppercase;color:#9a9a9a;padding:0 14px;margin:12px 0 6px;}" +
      ".dm-pop-links{padding:0 14px 12px;}" +
      ".dm-pop-links .badge{margin:0 5px 5px 0;}" +
      ".dm-pop-foot{display:flex;gap:8px;padding:12px 14px;border-top:1px solid #e3e1da;}" +
      ".dm-pop-foot .btn{flex:1;justify-content:center;}" +
      ".dm-pop-del{border-color:#dee2e6!important;color:#9a9a9a!important;background:#fff!important;}" +
      ".dm-pop-del:hover{background:#ed4033!important;border-color:#ed4033!important;color:#fff!important;}" +
      "#mapFeatureEditModal .trumbowyg-editor{min-height:120px;}" +
      ".dynamic-map-drawer .dm-drawer-item{display:flex;align-items:center;gap:6px;padding:6px 8px;border:0.5px solid rgba(0,0,0,0.15);border-radius:6px;margin-bottom:5px;font-size:13px;cursor:grab;background:#fff;}" +
      ".dynamic-map-drawer .dm-drawer-item.placed{opacity:0.55;}" +
      ".dynamic-map-drawer .dm-drawer-item.armed{outline:2px solid #2d7ff9;outline-offset:1px;}" +
      ".dynamic-map-drawer .dm-placed{margin-left:auto;font-size:11px;color:#888;}" +
      ".dm-delete-mode .leaflet-container,.dm-delete-mode .leaflet-interactive{cursor:url(\"data:image/svg+xml;base64," +
      "PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMCIgaGVpZ2h0PSIzMCIgdmlld0JveD0iMCAwIDI0IDI0Ij48Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSIxMSIgZmlsbD0iI2MwMzkyYiIgc3Ryb2tlPSIjZmZmZmZmIiBzdHJva2Utd2lkdGg9IjEiLz48ZyBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMS42IiBmaWxsPSJub25lIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik03IDhoMTAiLz48cGF0aCBkPSJNMTAgOFY2LjhjMC0uNS40LS45LjktLjloMi4yYy41IDAgLjkuNC45LjlWOCIvPjxwYXRoIGQ9Ik04LjIgOGwuNyA5YzAgLjUuNS45IDEgLjloNC4yYy41IDAgLjktLjQgMS0uOWwuNy05Ii8+PC9nPjwvc3ZnPgo=" +
      "\") 15 15,pointer !important;}"
    const tag = document.createElement("style")
    tag.id = "dynamic-map-styles"
    tag.textContent = css
    document.head.appendChild(tag)
  }

  initMap() {
    const h = parseInt(this.data.get("height"), 10)
    const w = parseInt(this.data.get("width"), 10)
    const imageUrl = this.data.get("imageUrl")
    if (!h || !w || !imageUrl) {
      console.warn("[dynamic-map] missing base image dimensions or url")
      return
    }
    this.imgH = h
    this.imgW = w

    const bounds = [[0, 0], [h, w]]
    this.map = L.map(this.canvasTarget, {
      crs: L.CRS.Simple,
      minZoom: -5,
      maxZoom: 4,
      // zoomSnap 0 lets fitBounds use a fractional zoom so the image fills the canvas
      // instead of snapping down a whole level (which left it small and centered).
      zoomSnap: 0,
      zoomDelta: 0.5,
      attributionControl: false
    })
    L.imageOverlay(imageUrl, bounds).addTo(this.map)
    // Pad the pan limit beyond the image so features (and their popups) near the
    // edge can be scrolled — and auto-panned — into view instead of snapping back
    // at the image border. Still bounded, so the map can't be lost in the void.
    this.map.setMaxBounds(L.latLngBounds(bounds).pad(0.5))
    this.fitToImage(bounds)

    // Fog: own pane above markers, rendered on a padded canvas so it doesn't flicker
    // (and thus expose the map) during zoom.
    this.map.createPane("fogPane")
    this.map.getPane("fogPane").style.zIndex = 640
    this.map.getPane("fogPane").style.pointerEvents = "none"
    this.fogRenderer = L.canvas({ pane: "fogPane", padding: 1 })

    // Cover the map with fog up front (before features load) so a player never
    // sees the bare image in the gap between the image painting and the features
    // request. Once reveals arrive, renderFog() runs again and cuts the holes.
    this.renderFog()

    // Wire popup buttons here (fires once the popup DOM exists) rather than right
    // after openPopup(), where the element isn't reliably present on reopen.
    this.map.on("popupopen", (e) => this.wirePopup(e))

    if (this.editable) {
      // Drag-to-link drop target, and tap-to-place fallback (touch).
      this.canvasTarget.addEventListener("dragover", (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy" })
      this.canvasTarget.addEventListener("drop", (e) => this.onCanvasDrop(e))
      this.map.on("click", (e) => this.onMapClick(e))
    }

    this.loadFeatures()
    if (this.editable) this.enableDrawing()
  }

  // Fit the whole image to the canvas on load. Leaflet often measures the canvas
  // before it has its final size, leaving the map tiny and centered — so we re-fit
  // via a ResizeObserver the first time the canvas reports real dimensions, and via
  // timer fallbacks for browsers without ResizeObserver.
  fitToImage(bounds) {
    const fit = () => {
      if (!this.map) return
      this.map.invalidateSize(false)
      this.map.fitBounds(bounds, { animate: false })
    }
    fit()
    requestAnimationFrame(fit)
    setTimeout(fit, 300)

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        const el = this.canvasTarget
        if (el && el.clientWidth > 0 && el.clientHeight > 0) {
          fit()
          // Fit only once, so later window resizes don't reset the user's zoom/pan.
          this.resizeObserver.disconnect()
          this.resizeObserver = null
        }
      })
      this.resizeObserver.observe(this.canvasTarget)
    }
  }

  // ---- read ----
  loadFeatures() {
    if (!this.featuresUrl) return
    fetch(this.featuresUrl, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : []))
      .then((features) => {
        (features || []).forEach((f) => this.buildFeatureLayer(f))
        this.applyFogScoping()
        this.renderFog()
      })
      .catch((e) => console.error("[dynamic-map] features load failed", e))
  }

  // Build a Leaflet layer from a feature object, attach it, and return it.
  buildFeatureLayer(f) {
    if (!f || !f.geometry || !f.geometry.type) return null
    let made = null
    const parsed = L.geoJSON(f.geometry, {
      pointToLayer: (_pt, latlng) => L.marker(latlng, { icon: f.feature_type === "text" ? this.textIcon(f) : this.pinIcon(f) }),
      style: () => this.styleFor(f)
    })
    parsed.eachLayer((layer) => {
      this.attachLayer(layer, f)
      made = layer
    })
    return made
  }

  attachLayer(layer, feature) {
    layer.__feature = feature
    layer.__featureId = feature.id
    layer.__lastGeometry = feature.geometry
    layer.addTo(this.map)
    this.registerEditable(layer)
    if (feature.feature_type === "reveal") {
      if (layer.setStyle) layer.setStyle(this.revealStyle())
      this.revealLayers.add(layer)
    } else {
      this.featureLayers.add(layer)
      this.setLabel(layer, feature)
      this.updateSecretMarker(layer)
      layer.on("click", (ev) => this.maybeOpenPopup(layer, ev))
    }
  }

  setLabel(layer, feature) {
    if (layer.unbindTooltip) layer.unbindTooltip()
    if (feature.feature_type === "text") return // text shows its content via the icon itself
    if (!feature.label) return
    // Secret features (only ever sent to GMs) show the standard secret icon in the name.
    const secret = feature.pc_secret ? `<i class="${SECRET_ICON}" title="Secret — only GMs see this" style="margin-right:3px;"></i>` : ""
    const content = secret + this.escapeHtml(feature.label)
    if (feature.feature_type === "pin") {
      layer.bindTooltip(content, { permanent: true, direction: "bottom", offset: [0, 2], className: "dynamic-map-label" })
    } else {
      layer.bindTooltip(content, { direction: "top", className: "dynamic-map-label" })
    }
  }

  styleFor(f) {
    return {
      color: f.stroke_color || DEFAULT_COLOR,
      weight: f.stroke_width || 2,
      fillColor: f.fill_color || f.stroke_color || DEFAULT_COLOR,
      fillOpacity: f.fill_opacity != null ? f.fill_opacity : 0.35
    }
  }

  revealStyle() {
    return { color: REVEAL_COLOR, weight: 1, fillColor: REVEAL_COLOR, fillOpacity: 0.22 }
  }

  pinIcon(f) {
    const color = (f && f.stroke_color) || DEFAULT_COLOR
    const faClass = (f && f.icon_name) || "fas fa-map-marker-alt"
    const outline = this.isWhite(color) ? "#000000" : "#ffffff" // keep a white pin visible
    const style = `font-size:32px;color:${color};-webkit-text-stroke:1.5px ${outline};text-shadow:0 2px 3px rgba(0,0,0,0.55);line-height:1;`
    // If a secret pin has no label (so no name to carry the icon), badge it on the pin.
    const secret = f && f.pc_secret && !f.label
      ? `<i class="${SECRET_ICON}" style="position:absolute;top:-2px;right:-7px;font-size:14px;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,0.85);"></i>`
      : ""
    return L.divIcon({
      className: "dynamic-map-pin",
      html: `<span style="position:relative;display:inline-block;"><i class="${faClass}" style="${style}"></i>${secret}</span>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
      popupAnchor: [0, -30]
    })
  }

  // Text label rendered directly on the map (its own feature type). The label IS the
  // text; styled by the chosen color, with a light halo so it reads over the map.
  textIcon(f) {
    const color = (f && f.stroke_color) || "#3a2c18"
    const o = this.isWhite(color) ? "#000" : "#fff" // white text gets a black outline
    const stroke = `-1px -1px 0 ${o},0 -1px 0 ${o},1px -1px 0 ${o},1px 0 0 ${o},1px 1px 0 ${o},0 1px 0 ${o},-1px 1px 0 ${o},-1px 0 0 ${o}`
    const text = f && f.label ? this.escapeHtml(f.label) : '<span style="opacity:0.5;">(text)</span>'
    const secret = f && f.pc_secret ? `<i class="${SECRET_ICON}" style="margin-right:3px;"></i>` : ""
    const html = `<span style="font-size:16px;font-weight:600;line-height:1.1;white-space:nowrap;color:${color};text-shadow:${stroke};">${secret}${text}</span>`
    return L.divIcon({ className: "dynamic-map-text", html, iconSize: null })
  }

  isWhite(color) {
    return /^#?f{3}(f{3})?$/i.test(String(color || "").trim())
  }

  // Secret marker (centered icon) for shapes/lines — pins carry the secret icon in
  // their name/badge instead. Rebuilt whenever pc_secret or geometry changes.
  updateSecretMarker(layer) {
    if (layer.__secretMarker) {
      this.map.removeLayer(layer.__secretMarker)
      layer.__secretMarker = null
    }
    const f = layer.__feature || {}
    // pins/text carry the secret icon inline; reveals don't show it.
    if (!f.pc_secret || f.feature_type === "pin" || f.feature_type === "text" || f.feature_type === "reveal") return
    const latlng = this.featureCenter(layer)
    if (!latlng) return
    const icon = L.divIcon({
      className: "dynamic-map-secret",
      html: `<i class="${SECRET_ICON}" title="Secret — only GMs see this" style="font-size:18px;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.85);"></i>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    })
    layer.__secretMarker = L.marker(latlng, { icon, interactive: false, keyboard: false, pmIgnore: true })
    if (this.map.hasLayer(layer)) layer.__secretMarker.addTo(this.map)
  }

  featureCenter(layer) {
    if (layer.getBounds) return layer.getBounds().getCenter()
    if (layer.getLatLng) return layer.getLatLng()
    return null
  }

  // ---- toolbar (cartographers) ----
  enableDrawing() {
    this.map.pm.setGlobalOptions({ markerStyle: { icon: this.pinIcon(null) } })
    this.map.on("pm:create", (e) => this.onCreate(e))
    this.map.on("pm:remove", (e) => this.onRemove(e))
    const resync = () => this.refreshToolFromPm()
    this.map.on("pm:globaleditmodetoggled", resync)
    this.map.on("pm:globaldragmodetoggled", resync)
    this.map.on("pm:globalremovalmodetoggled", resync)
    this.map.on("pm:drawend", () => this.refreshToolFromPm())
    this.highlightTool("select")
    this.refreshUndoButtons()
  }

  selectTool(e) {
    this.activateTool(e.currentTarget.getAttribute("data-tool"))
  }

  activateTool(name) {
    this.disableAllModes()
    switch (name) {
      case "pin": this.map.pm.enableDraw("Marker"); break
      case "text": this.textMode = true; this.map.pm.enableDraw("Marker"); break
      case "polygon": this.map.pm.enableDraw("Polygon"); break
      case "line": this.map.pm.enableDraw("Line"); break
      case "rectangle": this.map.pm.enableDraw("Rectangle"); break
      case "edit": this.map.pm.enableGlobalEditMode(); break
      case "drag": this.map.pm.enableGlobalDragMode(); break
      case "remove": this.map.pm.enableGlobalRemovalMode(); break
      default: break
    }
    this.highlightTool(name)
  }

  disableAllModes() {
    const pm = this.map.pm
    pm.disableDraw()
    if (pm.globalEditModeEnabled && pm.globalEditModeEnabled()) pm.disableGlobalEditMode()
    if (pm.globalDragModeEnabled && pm.globalDragModeEnabled()) pm.disableGlobalDragMode()
    if (pm.globalRemovalModeEnabled && pm.globalRemovalModeEnabled()) pm.disableGlobalRemovalMode()
    this.textMode = false
  }

  refreshToolFromPm() {
    const pm = this.map.pm
    let name = "select"
    if (pm.globalEditModeEnabled && pm.globalEditModeEnabled()) name = "edit"
    else if (pm.globalDragModeEnabled && pm.globalDragModeEnabled()) name = "drag"
    else if (pm.globalRemovalModeEnabled && pm.globalRemovalModeEnabled()) name = "remove"
    this.highlightTool(name)
  }

  highlightTool(name) {
    this.element.querySelectorAll("[data-tool]").forEach((btn) => {
      const tool = btn.getAttribute("data-tool")
      const on = tool === name
      // Tool buttons are the standard blue-outline .btn; the active tool fills
      // ocean via .is-active. Delete fills red (.is-active-danger) when active.
      btn.classList.toggle("is-active", on)
      btn.classList.toggle("is-active-danger", on && tool === "remove")
    })
    const deleting = name === "remove"
    this.element.classList.toggle("dm-delete-mode", deleting)
    this.setDeleteHints(deleting)
  }

  // Native hover hint on each feature while Delete is active, e.g. "Delete this pin".
  setDeleteHints(on) {
    const apply = (layer) => {
      const el = layer.getElement ? layer.getElement() : null
      if (!el) return
      if (on) el.setAttribute("title", `Delete this ${this.featureNoun(layer.__feature)}`)
      else el.removeAttribute("title")
    }
    this.featureLayers.forEach(apply)
    this.revealLayers.forEach(apply)
  }

  featureNoun(f) {
    switch (f && f.feature_type) {
      case "pin": return "pin"
      case "polygon":
      case "rectangle": return "area"
      case "polyline": return "line"
      case "reveal": return "reveal area"
      default: return "feature"
    }
  }

  // ---- create / delete ----
  onCreate(e) {
    const layer = e.layer
    // While in Edit Fog of War mode, anything drawn becomes a reveal region.
    const wasText = this.textMode
    const featureType = this.fogEditMode ? "reveal" : wasText ? "text" : this.shapeToType(e.shape)
    if (!featureType) {
      this.map.removeLayer(layer)
      return
    }
    if (e.shape === "Marker") layer.setIcon(featureType === "text" ? this.textIcon(null) : this.pinIcon(null))
    if (featureType === "reveal" && layer.setStyle) layer.setStyle(this.revealStyle())

    const geometry = layer.toGeoJSON().geometry
    this.request("POST", this.featuresUrl, { map_feature: { feature_type: featureType, geometry } })
      .then((r) => r.json())
      .then((saved) => {
        layer.__feature = saved
        layer.__featureId = saved.id
        layer.__lastGeometry = saved.geometry
        this.registerEditable(layer)
        if (featureType === "reveal") {
          this.revealLayers.add(layer)
          this.setLayerEditable(layer, true) // editable now (we're in fog edit mode)
          this.renderFog()
        } else {
          this.featureLayers.add(layer)
          layer.on("click", (ev) => this.maybeOpenPopup(layer, ev))
          this.renderFog()
          // A new text label starts empty — open the editor so the GM types its text.
          if (featureType === "text") this.openEditor(layer)
        }
        this.pushHistory({ kind: "create", layer, feature: saved })
      })
      .catch((err) => console.error("[dynamic-map] create failed", err))
  }

  registerEditable(layer) {
    if (!this.editable) return
    layer.on("pm:update", () => this.persistGeometry(layer))
    layer.on("pm:dragend", () => this.persistGeometry(layer))
  }

  persistGeometry(layer) {
    if (!layer.__featureId) return
    const before = layer.__lastGeometry
    const after = layer.toGeoJSON().geometry
    layer.__lastGeometry = after
    this.request("PATCH", `${this.featuresUrl}/${layer.__featureId}`, { map_feature: { geometry: after } })
      .then(() => {
        this.updateSecretMarker(layer) // reposition the secret icon to the new center
        if (layer.__feature && layer.__feature.feature_type === "reveal") this.renderFog()
        if (before) this.pushHistory({ kind: "geometry", layer, before, after })
      })
      .catch((err) => console.error("[dynamic-map] update failed", err))
  }

  onRemove(e) {
    this.deleteLayer(e.layer, { fromGeoman: true })
  }

  // User-initiated delete (Geoman trash, popup, or modal). Records undo history.
  deleteLayer(layer, opts) {
    opts = opts || {}
    if (!layer || !layer.__featureId) return
    const snapshot = layer.__feature
    const wasReveal = snapshot && snapshot.feature_type === "reveal"
    this.request("DELETE", `${this.featuresUrl}/${layer.__featureId}`)
      .then(() => {
        if (!opts.fromGeoman && this.map.hasLayer(layer)) this.map.removeLayer(layer)
        this.removeSecretMarker(layer)
        this.revealLayers.delete(layer)
        this.featureLayers.delete(layer)
        if (wasReveal) this.renderFog()
        if (this.editingLayer === layer) this.hideModal()
        this.pushHistory({ kind: "delete", feature: snapshot, layer: null })
      })
      .catch((err) => console.error("[dynamic-map] delete failed", err))
  }

  // ---- undo / redo (add + delete) ----
  pushHistory(entry) {
    this.undoStack.push(entry)
    this.redoStack = []
    this.refreshUndoButtons()
  }

  undo() {
    const entry = this.undoStack.pop()
    if (!entry) return
    this.invert(entry, true).then(() => {
      this.redoStack.push(entry)
      this.refreshUndoButtons()
    })
  }

  redo() {
    const entry = this.redoStack.pop()
    if (!entry) return
    this.invert(entry, false).then(() => {
      this.undoStack.push(entry)
      this.refreshUndoButtons()
    })
  }

  // geometry moves restore the before/after geometry; create/delete add or remove.
  invert(entry, undoing) {
    if (entry.kind === "geometry") {
      return this.historyApplyGeometry(entry, undoing ? entry.before : entry.after)
    }
    const shouldDelete = (entry.kind === "create") === undoing
    return shouldDelete ? this.historyDelete(entry) : this.historyRecreate(entry)
  }

  historyApplyGeometry(entry, geometry) {
    const layer = entry.layer
    if (!layer || !layer.__featureId || !geometry) return Promise.resolve()
    this.applyGeometryToLayer(layer, geometry)
    layer.__lastGeometry = geometry
    return this.request("PATCH", `${this.featuresUrl}/${layer.__featureId}`, { map_feature: { geometry } })
      .then(() => { if (layer.__feature && layer.__feature.feature_type === "reveal") this.renderFog() })
      .catch((err) => console.error("[dynamic-map] undo/redo move failed", err))
  }

  // Apply a GeoJSON geometry ([x,y]) back onto an existing layer ([lat,lng] = [y,x]).
  applyGeometryToLayer(layer, geometry) {
    const c = geometry.coordinates
    if (geometry.type === "Point" && layer.setLatLng) {
      layer.setLatLng([c[1], c[0]])
    } else if (geometry.type === "LineString" && layer.setLatLngs) {
      layer.setLatLngs(c.map((p) => [p[1], p[0]]))
    } else if (geometry.type === "Polygon" && layer.setLatLngs) {
      layer.setLatLngs((c[0] || []).map((p) => [p[1], p[0]]))
    }
  }

  historyDelete(entry) {
    const layer = entry.layer
    if (!layer || !layer.__featureId) return Promise.resolve()
    const wasReveal = layer.__feature && layer.__feature.feature_type === "reveal"
    return this.request("DELETE", `${this.featuresUrl}/${layer.__featureId}`)
      .then(() => {
        if (this.map.hasLayer(layer)) this.map.removeLayer(layer)
        this.removeSecretMarker(layer)
        this.revealLayers.delete(layer)
        this.featureLayers.delete(layer)
        if (wasReveal) this.renderFog()
      })
      .catch((err) => console.error("[dynamic-map] undo/redo delete failed", err))
  }

  historyRecreate(entry) {
    const f = entry.feature
    return this.request("POST", this.featuresUrl, { map_feature: this.recreatePayload(f) })
      .then((r) => r.json())
      .then((saved) => {
        const layer = this.buildFeatureLayer(saved)
        entry.layer = layer
        entry.feature = saved
        if (saved.feature_type === "reveal") this.renderFog()
      })
      .catch((err) => console.error("[dynamic-map] undo/redo recreate failed", err))
  }

  recreatePayload(f) {
    return {
      feature_type: f.feature_type,
      geometry: f.geometry,
      label: f.label,
      notes: f.notes,
      stroke_color: f.stroke_color,
      fill_color: f.fill_color,
      fill_opacity: f.fill_opacity,
      stroke_width: f.stroke_width,
      icon_id: f.icon_id,
      pc_secret: f.pc_secret,
      reveal_status: f.reveal_status
    }
  }

  refreshUndoButtons() {
    const u = this.ctlEl("undoBtn")
    const r = this.ctlEl("redoBtn")
    if (u) u.disabled = this.undoStack.length === 0
    if (r) r.disabled = this.redoStack.length === 0
  }

  // ---- fog of war ----
  toggleFog(e) {
    this.fogEnabled = e.target.checked
    const show = this.fogEnabled ? "" : "none"
    ;["fogEditBtn", "fogShowWrap", "previewWrap", "fogHelp"].forEach((n) => {
      const el = this.ctlEl(n)
      if (el) el.style.display = show
    })
    if (this.fogEnabled) {
      const s = this.ctlEl("fogShow")
      if (s) s.checked = true
      this.fogShow = true
      const p = this.ctlEl("preview")
      if (p) p.checked = false
      this.preview = false
    } else {
      // Turning fog off also exits fog-edit mode and unlocks everything.
      this.fogEditMode = false
      this.applyFogScoping()
      this.updateFogEditUI()
    }
    this.persistFog()
    this.renderFog()
  }

  // GM-only: transparent fog overlay while editing (still see/edit the whole map).
  toggleFogShow(e) {
    this.fogShow = e.target.checked
    this.renderFog()
  }

  // GM-only: exact player view (opaque fog, under-fog content hidden).
  togglePreview(e) {
    this.preview = e.target.checked
    this.renderFog()
  }

  // Toggle a dedicated mode where ONLY fog reveals are editable: other features are
  // locked + dimmed so they don't compete, and reveals become interactive.
  toggleFogEdit() {
    this.fogEditMode = !this.fogEditMode
    if (this.fogEditMode) {
      this.preview = false
      const p = this.ctlEl("preview")
      if (p) p.checked = false
      this.fogShow = true
      const s = this.ctlEl("fogShow")
      if (s) s.checked = true
    }
    this.disableAllModes()
    this.highlightTool("select")
    this.applyFogScoping()
    this.updateFogEditUI()
    this.renderFog()
  }

  // Lock the set that shouldn't be edited (Geoman pmIgnore + non-interactive), and
  // dim the locked regular features while editing fog.
  applyFogScoping() {
    const fog = this.fogEditMode
    this.featureLayers.forEach((l) => { this.setLayerEditable(l, !fog); this.setLayerDim(l, fog) })
    this.revealLayers.forEach((l) => { this.setLayerEditable(l, fog); this.setLayerDim(l, false) })
  }

  setLayerEditable(layer, editable) {
    layer.options.pmIgnore = !editable
    if (!editable && layer.pm && layer.pm.enabled && layer.pm.enabled()) layer.pm.disable()
    const el = layer.getElement && layer.getElement()
    if (el) el.style.pointerEvents = editable ? "" : "none"
  }

  setLayerDim(layer, dim) {
    const el = layer.getElement && layer.getElement()
    if (el) el.style.opacity = dim ? "0.35" : ""
  }

  updateFogEditUI() {
    const btn = this.ctlEl("fogEditBtn")
    if (btn) btn.classList.toggle("is-active", this.fogEditMode)
    // Pin/Line/Box don't apply to fog regions — disable them while editing fog.
    ;["pin", "line", "rectangle", "text"].forEach((t) => {
      const b = this.element.querySelector(`[data-tool="${t}"]`)
      if (b) {
        b.disabled = this.fogEditMode
        b.style.opacity = this.fogEditMode ? "0.4" : ""
      }
    })
  }

  // Three render modes:
  //   "off"     — no fog at all (also hides reveal outlines).
  //   "editFog" — GM editing: a TRANSPARENT fog you can still see/edit through;
  //               reveals shown as green outlines so they're visible and editable.
  //   "preview" — exact player view: opaque fog, under-fog content hidden, reveals
  //               are just clear holes. Players always get this when fog is enabled.
  fogMode() {
    if (!this.fogEnabled) return "off"
    if (!this.isGm) return "preview"
    if (this.fogEditMode) return "editFog" // editing fog always shows it transparently
    if (this.preview) return "preview"
    if (this.fogShow) return "editFog"
    return "off"
  }

  collectRevealRings() {
    const rings = []
    this.revealLayers.forEach((layer) => {
      const r = this.outerRing(layer.toGeoJSON().geometry)
      if (r) rings.push(r)
    })
    return rings
  }

  renderFog() {
    const mode = this.fogMode()

    this.revealLayers.forEach((layer) => {
      if (!layer.setStyle) return
      // Dashed outline so it's obvious a reveal can be selected / moved / reshaped.
      if (mode === "editFog") layer.setStyle({ color: REVEAL_COLOR, weight: 2, dashArray: "6 5", fillColor: REVEAL_COLOR, fillOpacity: 0.05 })
      else layer.setStyle({ opacity: 0, fillOpacity: 0 })
    })

    this.updateFeatureVisibility(mode)

    if (this.fogLayer) {
      this.map.removeLayer(this.fogLayer)
      this.fogLayer = null
    }
    if (this.fogLabel) {
      this.map.removeLayer(this.fogLabel)
      this.fogLabel = null
    }
    if (mode === "off") return

    const opacity = mode === "preview" ? this.fogOpacity : Math.min(this.fogOpacity, 0.4)
    const outer = [[0, 0], [0, this.imgW], [this.imgH, this.imgW], [this.imgH, 0]]
    const holes = this.collectRevealRings().map((ring) => ring.map((c) => [c[1], c[0]]))
    this.fogLayer = L.polygon([outer, ...holes], {
      renderer: this.fogRenderer,
      pane: "fogPane",
      stroke: false,
      fillColor: this.fogColor,
      fillOpacity: opacity,
      interactive: false,
      pmIgnore: true,   // keep Geoman off the fog so it can't be edited instead of reveals
      snapIgnore: true
    })
    this.fogLayer.addTo(this.map)

    const labelIcon = L.divIcon({ className: "dynamic-map-fog-label", html: "Fog of War", iconSize: [180, 30], iconAnchor: [90, 15] })
    this.fogLabel = L.marker([this.imgH / 2, this.imgW / 2], { icon: labelIcon, pane: "fogPane", interactive: false, keyboard: false, pmIgnore: true })
    this.fogLabel.addTo(this.map)
  }

  // Show/hide each annotation based on its category toggle AND fog. Under-fog hiding
  // applies only in the true player preview.
  updateFeatureVisibility(mode) {
    mode = mode || this.fogMode()
    const hideUnderFog = mode === "preview"
    const rings = hideUnderFog ? this.collectRevealRings() : []
    this.featureLayers.forEach((layer) => {
      const f = layer.__feature || {}
      const catOn = this.categoryEnabled(f.feature_type)
      const fogOk = !hideUnderFog || this.pointInAnyRing(this.repPoint(layer.toGeoJSON().geometry), rings)
      this.setLayerOnMap(layer, catOn && fogOk)
    })
  }

  setLayerOnMap(layer, show) {
    if (show) {
      if (!this.map.hasLayer(layer)) layer.addTo(this.map)
      if (layer.__secretMarker && !this.map.hasLayer(layer.__secretMarker)) layer.__secretMarker.addTo(this.map)
    } else {
      if (this.map.hasLayer(layer)) this.map.removeLayer(layer)
      if (layer.__secretMarker && this.map.hasLayer(layer.__secretMarker)) this.map.removeLayer(layer.__secretMarker)
    }
  }

  removeSecretMarker(layer) {
    if (layer.__secretMarker) {
      this.map.removeLayer(layer.__secretMarker)
      layer.__secretMarker = null
    }
  }

  // View toggles (any viewer): pins, shapes (polygon/rectangle/line), text.
  categoryEnabled(type) {
    if (type === "pin") return this.categories.pin
    if (type === "text") return this.categories.text
    return this.categories.shape
  }

  toggleCategory(e) {
    const cat = e.currentTarget.getAttribute("data-cat")
    if (cat in this.categories) this.categories[cat] = e.target.checked
    this.updateFeatureVisibility()
  }

  outerRing(geometry) {
    if (geometry && geometry.type === "Polygon" && geometry.coordinates) {
      return geometry.coordinates[0]
    }
    return null
  }

  repPoint(geometry) {
    if (!geometry) return null
    const c = geometry.coordinates
    switch (geometry.type) {
      case "Point": return c
      case "LineString": return c && c[0]
      case "Polygon": return c && c[0] && c[0][0]
      default: return null
    }
  }

  pointInAnyRing(point, rings) {
    if (!point || !rings.length) return false
    return rings.some((ring) => this.pointInRing(point, ring))
  }

  pointInRing(point, ring) {
    if (!ring || ring.length < 3) return false
    const x = point[0]
    const y = point[1]
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1]
      const xj = ring[j][0], yj = ring[j][1]
      const denom = (yj - yi) || 1e-12
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / denom + xi)) inside = !inside
    }
    return inside
  }

  persistFog() {
    if (!this.updateUrl) return
    const token = document.querySelector('meta[name="csrf-token"]')
    fetch(this.updateUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ map: { fog_enabled: this.fogEnabled } })
    }).catch((err) => console.error("[dynamic-map] fog persist failed", err))
  }

  // ---- read popup (all viewers) ----
  maybeOpenPopup(layer, e) {
    if (layer.__feature && layer.__feature.feature_type === "reveal") return
    if (this.editable && this.anyPmModeActive()) return
    this.openFeaturePopup(layer, e)
  }

  anyPmModeActive() {
    const pm = this.map.pm
    return !!(
      (pm.globalDrawModeEnabled && pm.globalDrawModeEnabled()) ||
      (pm.globalEditModeEnabled && pm.globalEditModeEnabled()) ||
      (pm.globalRemovalModeEnabled && pm.globalRemovalModeEnabled()) ||
      (pm.globalDragModeEnabled && pm.globalDragModeEnabled())
    )
  }

  // A fresh standalone popup at the click point each time — not bound to the layer —
  // so there's no per-layer popup state that breaks on reopen.
  openFeaturePopup(layer, e) {
    this.popupLayer = layer
    const latlng =
      (e && e.latlng) ||
      (layer.getLatLng && layer.getLatLng()) ||
      (layer.getBounds && layer.getBounds().getCenter())
    if (!latlng) return
    const popup = L.popup({ className: "dynamic-map-popup", maxWidth: 300, autoPan: true, closeButton: false })
      .setLatLng(latlng)
      .setContent(this.popupHtml(layer.__feature || {}))
    this.map.openPopup(popup)
  }

  wirePopup(e) {
    const root = e.popup && e.popup.getElement()
    const layer = this.popupLayer
    if (!root || !layer) return
    const editBtn = root.querySelector('[data-pop="edit"]')
    if (editBtn) editBtn.onclick = (ev) => {
      ev.preventDefault()
      ev.stopPropagation()
      this.map.closePopup()
      // Defer so this click fully resolves before the modal opens — otherwise the
      // trailing event is read as a backdrop click and immediately hides the modal.
      setTimeout(() => this.openEditor(layer), 0)
    }
    const delBtn = root.querySelector('[data-pop="delete"]')
    if (delBtn) delBtn.onclick = (ev) => {
      ev.preventDefault()
      ev.stopPropagation()
      this.map.closePopup()
      if (window.confirm("Delete this feature?")) this.deleteLayer(layer)
    }
    const closeBtn = root.querySelector('[data-pop="close"]')
    if (closeBtn) closeBtn.onclick = (ev) => {
      ev.preventDefault()
      ev.stopPropagation()
      this.map.closePopup()
    }
  }

  popupHtml(f) {
    const title = f.label || this.titleCase(f.feature_type || "feature")
    const color = f.stroke_color || DEFAULT_COLOR
    const kind = this.popupKind(f.feature_type)
    const notes = f.notes && f.notes.trim() ? f.notes : '<span style="color:#999;">No notes yet.</span>'
    let linksHtml = ""
    const links = f.links || []
    if (links.length) {
      linksHtml =
        '<div class="dm-pop-linklabel">Linked</div><div class="dm-pop-links">' +
        links.map((l) => `<a href="${l.url || "#"}" class="badge badge-medium text-decoration-none" style="font-weight:normal;display:inline-block;">${this.escapeHtml(l.name || l.type)}</a>`).join("") +
        "</div>"
    }
    let actions = ""
    if (this.editable) {
      actions =
        '<div class="dm-pop-foot">' +
        '<button type="button" data-pop="edit" class="btn btn-green btn-sm"><i class="fal fa-pen"></i> Edit</button>' +
        '<button type="button" data-pop="delete" class="btn btn-sm dm-pop-del"><i class="fal fa-trash"></i> Delete</button>' +
        "</div>"
    }
    return (
      '<div class="dm-pop">' +
        `<div class="dm-pop-accent" style="background:${color};"></div>` +
        '<div class="dm-pop-inner">' +
          '<div class="dm-pop-hd">' +
            `<div class="dm-pop-heading"><div class="dm-pop-kind">${this.escapeHtml(kind)}</div><div class="dm-pop-title">${this.escapeHtml(title)}</div></div>` +
            '<button type="button" data-pop="close" class="dm-pop-x" title="Close" aria-label="Close">&times;</button>' +
          '</div>' +
          `<div class="dm-pop-body"><div class="dm-pop-notes">${notes}</div></div>` +
          linksHtml +
          actions +
        '</div>' +
      '</div>'
    )
  }

  // Friendly type label shown as the popup kicker.
  popupKind(type) {
    switch (type) {
      case "pin": return "Pin"
      case "polygon":
      case "rectangle": return "Area"
      case "polyline": return "Line"
      case "text": return "Text"
      default: return this.titleCase(type || "Feature")
    }
  }

  // ---- property editor modal (cartographers only) ----
  openEditor(layer) {
    if (!this.hasEditModalTarget) return
    this.editingLayer = layer
    const f = layer.__feature || {}
    const modal = this.editModalTarget

    this.setField("typeLabel", f.feature_type || "feature", true)
    this.fieldEl("label").value = f.label || ""

    this.selectedColor = f.stroke_color || DEFAULT_COLOR
    this.highlightColor()

    const isPin = f.feature_type === "pin"
    const iconRow = modal.querySelector('[data-mf="iconRow"]')
    if (iconRow) iconRow.style.display = isPin ? "" : "none"
    this.selectedIconId = f.icon_id ? String(f.icon_id) : ""
    this.highlightIcon()

    const secret = this.fieldEl("secret")
    if (secret) secret.checked = !!f.pc_secret

    this.renderLinkChips()
    const lr = this.fieldEl("linkResults")
    if (lr) { lr.style.display = "none"; lr.innerHTML = "" }
    const ls = this.fieldEl("linkSearch")
    if (ls) ls.value = ""

    this.showEditorModal()
    setTimeout(() => this.setNotes(f.notes || ""), 80)
  }

  // Open from a clean slate each time: dispose any stale Bootstrap instance and clear
  // orphaned backdrops/body state left by a previous cycle (otherwise a later show()
  // silently no-ops and the modal "won't open").
  showEditorModal() {
    const el = this.editModalTarget
    const existing = window.bootstrap.Modal.getInstance(el)
    if (existing) existing.dispose()
    document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove())
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("padding-right")
    new window.bootstrap.Modal(el).show()
  }

  saveFeature() {
    if (!this.editingLayer) return
    const f = this.editingLayer.__feature || {}
    const payload = {
      label: this.fieldEl("label").value,
      notes: this.getNotes(),
      stroke_color: this.selectedColor,
      fill_color: this.selectedColor
    }
    if (f.feature_type === "pin") payload.icon_id = this.selectedIconId || ""
    const secret = this.fieldEl("secret")
    if (secret) payload.pc_secret = secret.checked

    this.request("PATCH", `${this.featuresUrl}/${this.editingLayer.__featureId}`, { map_feature: payload })
      .then((r) => r.json())
      .then((saved) => {
        this.applyFeatureUpdate(this.editingLayer, saved)
        this.hideModal()
      })
      .catch((err) => console.error("[dynamic-map] save failed", err))
  }

  applyFeatureUpdate(layer, saved) {
    layer.__feature = saved
    if (saved.feature_type === "text" && layer.setIcon) {
      layer.setIcon(this.textIcon(saved))
    } else if (saved.feature_type === "pin" && layer.setIcon) {
      layer.setIcon(this.pinIcon(saved))
    } else if (layer.setStyle) {
      layer.setStyle(this.styleFor(saved))
    }
    this.setLabel(layer, saved)
    this.updateSecretMarker(layer)
  }

  deleteFeature() {
    if (this.editingLayer) this.deleteLayer(this.editingLayer)
  }

  hideModal() {
    if (this.hasEditModalTarget) {
      const inst = window.bootstrap.Modal.getInstance(this.editModalTarget)
      if (inst) inst.hide()
    }
    // editingLayer cleared + Trumbowyg torn down in afterModalHidden (covers all closes).
  }

  // ---- editor field helpers ----
  selectColor(e) {
    this.selectedColor = e.currentTarget.getAttribute("data-color") || DEFAULT_COLOR
    this.highlightColor()
  }

  selectIcon(e) {
    this.selectedIconId = e.currentTarget.getAttribute("data-icon-id") || ""
    this.highlightIcon()
  }

  filterIcons(e) {
    const q = (e.currentTarget.value || "").toLowerCase()
    this.editModalTarget.querySelectorAll(".mf-icon").forEach((btn) => {
      const d = (btn.getAttribute("data-descriptor") || "").toLowerCase()
      btn.style.display = !q || d.indexOf(q) !== -1 ? "" : "none"
    })
  }

  highlightColor() {
    this.editModalTarget.querySelectorAll(".mf-color").forEach((btn) => {
      const on = btn.getAttribute("data-color") === this.selectedColor
      btn.style.outline = on ? "3px solid rgba(0,0,0,0.55)" : "none"
      btn.style.outlineOffset = "1px"
    })
  }

  highlightIcon() {
    this.editModalTarget.querySelectorAll(".mf-icon").forEach((btn) => {
      const on = (btn.getAttribute("data-icon-id") || "") === (this.selectedIconId || "")
      btn.classList.toggle("btn-green", on)
      btn.classList.toggle("btn-light", !on)
    })
  }

  // ---- links (search / add / remove) ----
  searchLinkables() {
    clearTimeout(this.linkSearchTimer)
    this.linkSearchTimer = setTimeout(() => this.performLinkSearch(), 200)
  }

  performLinkSearch() {
    if (!this.linkablesUrl) return
    const type = this.fieldEl("linkType").value
    const q = this.fieldEl("linkSearch").value.trim()
    const url = `${this.linkablesUrl}?type=${encodeURIComponent(type)}&q=${encodeURIComponent(q)}`
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => this.renderLinkResults(items, type))
      .catch((err) => console.error("[dynamic-map] linkable search failed", err))
  }

  renderLinkResults(items, type) {
    const box = this.fieldEl("linkResults")
    if (!box) return
    if (!items.length) {
      box.style.display = "none"
      box.innerHTML = ""
      return
    }
    box.innerHTML = items.map((it) =>
      `<button type="button" class="list-group-item list-group-item-action py-1 px-2 font-size-13" data-action="click->dynamic-map#pickLinkable" data-link-id="${it.id}" data-link-name="${this.escapeHtml(it.name)}" data-link-type="${type}">${it.secret ? `<i class="${SECRET_ICON}" title="Secret \u2014 only GMs see this"></i> ` : ""}${this.escapeHtml(it.name)}</button>`
    ).join("")
    box.style.display = ""
  }

  pickLinkable(e) {
    const el = e.currentTarget
    this.addLink(el.getAttribute("data-link-type"), el.getAttribute("data-link-id"), el.getAttribute("data-link-name"))
    const box = this.fieldEl("linkResults")
    if (box) { box.style.display = "none"; box.innerHTML = "" }
    const s = this.fieldEl("linkSearch")
    if (s) s.value = ""
  }

  addLink(type, id) {
    if (!this.editingLayer) return
    const url = `${this.featuresUrl}/${this.editingLayer.__featureId}/links`
    this.request("POST", url, { type, id })
      .then((r) => r.json())
      .then((link) => {
        const f = this.editingLayer.__feature || (this.editingLayer.__feature = {})
        f.links = f.links || []
        f.links.push(link)
        this.renderLinkChips()
      })
      .catch((err) => console.error("[dynamic-map] add link failed", err))
  }

  removeLink(e) {
    const linkId = e.currentTarget.getAttribute("data-link-id")
    if (!this.editingLayer || !linkId) return
    const url = `${this.featuresUrl}/${this.editingLayer.__featureId}/links/${linkId}`
    this.request("DELETE", url)
      .then(() => {
        const f = this.editingLayer.__feature || {}
        f.links = (f.links || []).filter((l) => String(l.id) !== String(linkId))
        this.renderLinkChips()
      })
      .catch((err) => console.error("[dynamic-map] remove link failed", err))
  }

  renderLinkChips() {
    const box = this.fieldEl("linkChips")
    if (!box) return
    const links = (this.editingLayer && this.editingLayer.__feature && this.editingLayer.__feature.links) || []
    if (!links.length) {
      box.innerHTML = '<span class="text-muted font-size-12">No links yet.</span>'
      return
    }
    box.innerHTML = links.map((l) =>
      `<span class="badge bg-medium d-inline-flex align-items-center mr-1 mb-1" style="gap:5px;font-weight:normal;font-size:12px;">${this.escapeHtml(l.name || l.type)} <i class="fal fa-times" style="cursor:pointer;" data-action="click->dynamic-map#removeLink" data-link-id="${l.id}"></i></span>`
    ).join("")
  }

  // ---- drag-to-link drawer ----
  toggleDrawer() {
    const d = this.ctlEl("drawer")
    if (!d) return
    const open = d.style.display === "none" || d.style.display === ""
    d.style.display = open ? "flex" : "none"
    if (open) this.searchDrawer()
    else this.clearArmed()
  }

  searchDrawer() {
    clearTimeout(this.drawerTimer)
    this.drawerTimer = setTimeout(() => this.performDrawerSearch(), 200)
  }

  performDrawerSearch() {
    if (!this.linkablesUrl) return
    const type = this.ctlEl("drawerType").value
    const q = this.ctlEl("drawerSearch").value.trim()
    const url = `${this.linkablesUrl}?type=${encodeURIComponent(type)}&q=${encodeURIComponent(q)}`
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : []))
      .then((items) => this.renderDrawerResults(items, type))
      .catch((err) => console.error("[dynamic-map] drawer search failed", err))
  }

  renderDrawerResults(items, type) {
    const box = this.ctlEl("drawerResults")
    if (!box) return
    if (!items.length) {
      box.innerHTML = '<div class="text-muted font-size-12 p-1">No matches.</div>'
      return
    }
    box.innerHTML = items.map((it) => {
      const count = this.placedCount(type, it.id)
      const placed = count > 0
      const badge = placed ? `<span class="dm-placed"><i class="fal fa-check"></i>${count > 1 ? " ×" + count : ""}</span>` : ""
      return `<div class="dm-drawer-item ${placed ? "placed" : ""}" draggable="true" data-action="dragstart->dynamic-map#onItemDragStart click->dynamic-map#onItemTap" data-link-type="${type}" data-link-id="${it.id}" data-link-name="${this.escapeHtml(it.name)}"><i class="fal fa-grip-vertical" style="color:#aaa;"></i> ${it.secret ? `<i class="${SECRET_ICON}" title="Secret \u2014 only GMs see this"></i> ` : ""}<span>${this.escapeHtml(it.name)}</span> ${badge}</div>`
    }).join("")
  }

  placedCount(type, id) {
    let n = 0
    this.featureLayers.forEach((l) => {
      const links = (l.__feature && l.__feature.links) || []
      n += links.filter((lk) => lk.type === type && String(lk.target_id) === String(id)).length
    })
    return n
  }

  onItemDragStart(e) {
    const el = e.currentTarget
    const payload = JSON.stringify({ type: el.getAttribute("data-link-type"), id: el.getAttribute("data-link-id"), name: el.getAttribute("data-link-name") })
    if (e.dataTransfer) {
      e.dataTransfer.setData("text/plain", payload)
      e.dataTransfer.effectAllowed = "copy"
    }
  }

  onItemTap(e) {
    const el = e.currentTarget
    this.armedLinkable = { type: el.getAttribute("data-link-type"), id: el.getAttribute("data-link-id"), name: el.getAttribute("data-link-name") }
    this.element.querySelectorAll(".dm-drawer-item.armed").forEach((n) => n.classList.remove("armed"))
    el.classList.add("armed")
  }

  clearArmed() {
    this.armedLinkable = null
    this.element.querySelectorAll(".dm-drawer-item.armed").forEach((n) => n.classList.remove("armed"))
  }

  onMapClick(e) {
    if (!this.armedLinkable || this.anyPmModeActive()) return
    const a = this.armedLinkable
    this.clearArmed()
    this.dropLinkedPin(e.latlng, a)
  }

  onCanvasDrop(e) {
    e.preventDefault()
    let payload
    try {
      payload = JSON.parse(e.dataTransfer.getData("text/plain"))
    } catch (err) {
      return
    }
    if (!payload || !payload.type) return
    this.dropLinkedPin(this.map.mouseEventToLatLng(e), payload)
  }

  // Atomic create-with-link: one POST drops a pin (labeled with the entity) and links it.
  dropLinkedPin(latlng, link) {
    const geometry = { type: "Point", coordinates: [latlng.lng, latlng.lat] }
    this.request("POST", this.featuresUrl, {
      map_feature: { feature_type: "pin", geometry, label: link.name },
      link: { type: link.type, id: link.id }
    })
      .then((r) => r.json())
      .then((saved) => {
        const layer = this.buildFeatureLayer(saved)
        if (layer) {
          layer.__lastGeometry = saved.geometry
          this.pushHistory({ kind: "create", layer, feature: saved })
        }
        this.renderFog()
        this.searchDrawer() // refresh the "on map" indicators
      })
      .catch((err) => console.error("[dynamic-map] drop-link failed", err))
  }

  fieldEl(name) {
    return this.editModalTarget.querySelector(`[data-mf="${name}"]`)
  }

  ctlEl(name) {
    return this.element.querySelector(`[data-mf="${name}"]`)
  }

  setField(name, value, text) {
    const el = this.fieldEl(name)
    if (!el) return
    if (text) el.textContent = value
    else el.value = value
  }

  setNotes(html) {
    const $ = window.jQuery || window.$
    const node = document.getElementById("mfNotes")
    if ($ && $.fn && $.fn.trumbowyg && node) {
      try {
        $(node).trumbowyg("html", html || "")
        return
      } catch (e) { /* fall through */ }
    }
    if (node) node.innerHTML = html || ""
  }

  getNotes() {
    const $ = window.jQuery || window.$
    const node = document.getElementById("mfNotes")
    if ($ && $.fn && $.fn.trumbowyg && node) {
      try {
        return $(node).trumbowyg("html")
      } catch (e) { /* fall through */ }
    }
    return node ? node.innerHTML : ""
  }

  escapeHtml(s) {
    const d = document.createElement("div")
    d.textContent = String(s == null ? "" : s)
    return d.innerHTML
  }

  titleCase(s) {
    s = String(s || "")
    return s.charAt(0).toUpperCase() + s.slice(1)
  }

  shapeToType(shape) {
    switch (shape) {
      case "Marker": return "pin"
      case "Polygon": return "polygon"
      case "Rectangle": return "rectangle"
      case "Line": return "polyline"
      case "Text": return "text"
      default: return null
    }
  }

  request(method, url, body) {
    const token = document.querySelector('meta[name="csrf-token"]')
    return fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : ""
      },
      credentials: "same-origin",
      body: body ? JSON.stringify(body) : undefined
    }).then((r) => {
      if (!r.ok) throw new Error(`${method} ${url} -> ${r.status}`)
      return r
    })
  }
}
